<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isPortalPreviewStub } from '../lib/previewEventStub';
import EditorRenderFrame from './EditorRenderFrame.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  templateId: { type: String, default: '' },
  theme: { type: Object, default: () => ({}) },
  selectedEventId: { type: [String, Number], default: null },
  deviceMode: { type: String, default: 'desktop' },
  portalEditableAttributeKeys: { type: Array, default: () => [] },
  inboxId: { type: [String, Number], default: '' },
});

defineEmits(['update:deviceMode']);

const { t } = useI18n();
const fullscreen = ref(false);
const frameRef = ref(null);

const usingStub = computed(() => isPortalPreviewStub(props.selectedEventId));

const openInNewTab = () => {
  const html = frameRef.value?.iframeRef?.srcdoc || '';
  if (!html) return;
  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
};
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 overflow-hidden">
    <div
      class="flex items-center justify-between px-4 py-2 border-b border-n-slate-6 bg-n-slate-2 shrink-0"
    >
      <div class="min-w-0">
        <p
          class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
        >
          Preview
        </p>
        <p v-if="usingStub" class="text-[10px] text-n-slate-9 mt-0.5 truncate">
          {{ t('PLANNING.EVENT_PORTALS.EDITOR.SAMPLE_EVENT.BADGE') }}
        </p>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <button
          class="p-1.5 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
          :title="t('PLANNING.EVENT_PORTALS.EDITOR.OPEN_IN_BROWSER')"
          @click="openInNewTab"
        >
          <span class="i-lucide-external-link size-3.5" />
        </button>
        <button
          class="p-1.5 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
          title="Fullscreen preview"
          @click="fullscreen = true"
        >
          <span class="i-lucide-maximize-2 size-3.5" />
        </button>
      </div>
    </div>

    <div class="flex-1 min-h-0 overflow-hidden relative">
      <EditorRenderFrame
        v-if="selectedEventId"
        ref="frameRef"
        :template-id="templateId"
        :document="modelValue"
        :theme="theme"
        :selected-event-id="selectedEventId"
        :portal-editable-attribute-keys="portalEditableAttributeKeys"
        :inbox-id="inboxId"
        :device-mode="deviceMode"
        :annotate="false"
        :interactive="true"
      />
      <div
        v-else
        class="flex flex-col items-center justify-center h-full text-n-slate-9 gap-2 px-6 text-center"
      >
        <span class="i-lucide-eye size-8 text-n-slate-7" />
        <p class="text-sm">Select an event in the sidebar to preview</p>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="fullscreen"
        class="fixed inset-0 z-[100] flex flex-col bg-n-slate-1"
      >
        <div
          class="flex items-center justify-between px-4 py-2 border-b border-n-slate-6 bg-n-slate-2 shrink-0"
        >
          <p
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
          >
            Fullscreen Preview
          </p>
          <div class="flex items-center gap-2">
            <button
              class="p-1.5 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
              :title="t('PLANNING.EVENT_PORTALS.EDITOR.OPEN_IN_BROWSER')"
              @click="openInNewTab"
            >
              <span class="i-lucide-external-link size-3.5" />
            </button>
            <button
              class="p-1.5 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-3 transition-colors"
              title="Close fullscreen"
              @click="fullscreen = false"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>
        </div>
        <div class="flex-1 min-h-0 overflow-hidden bg-n-slate-3">
          <div class="max-w-3xl mx-auto h-full">
            <EditorRenderFrame
              :template-id="templateId"
              :document="modelValue"
              :theme="theme"
              :selected-event-id="selectedEventId"
              :portal-editable-attribute-keys="portalEditableAttributeKeys"
              :inbox-id="inboxId"
              :device-mode="deviceMode"
              :annotate="false"
              :interactive="true"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
